import { useEffect, useMemo, useState } from 'react';
import {
  Modal, TextInput, Stack, Group, Button, Alert, Select, Autocomplete, Switch,
  Collapse, Paper, Table, Text, Loader, Center,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import ImageUpload from '../../components/ImageUpload';
import { IconAdd as IconPlus } from '../../components/icons/CncIcons';
import { api } from '../../api/client';
import { useAuth, canWrite } from '../../store/auth';
import { folderNameError } from '../../lib/nameValidation';
import { COUNTRY_NAMES, countiesFor } from '../../lib/geo';
import type { Customer } from './CustomersPage';
import ContactModal, { type Contact } from './ContactModal';

interface CustomerDetail extends Customer {
  contacts: Contact[];
}

interface AddressState {
  country: string | null;
  county: string | null;
  street: string | null;
  zip: string | null;
  countyManual: boolean;
}

interface LegalState extends AddressState {
  companyName: string | null;
  vatNumber: string | null;
}

function emptyAddress(): AddressState {
  return { country: null, county: null, street: null, zip: null, countyManual: false };
}

function AddressEditor({
  title,
  value,
  onChange,
  streetSuggestions,
  disabled,
}: {
  title: string;
  value: AddressState;
  onChange: (v: AddressState) => void;
  streetSuggestions: string[];
  disabled: boolean;
}) {
  const counties = countiesFor(value.country);
  // Use the Select when a preset list exists AND the user hasn't opted into manual entry.
  const showList = !!counties && counties.length > 0 && !value.countyManual;

  return (
    <Stack gap="xs">
      {title && <Text size="sm" fw={600}>{title}</Text>}
      <Select
        label="Country"
        placeholder="Select country"
        data={COUNTRY_NAMES}
        searchable
        clearable
        value={value.country}
        onChange={(v) => onChange({ ...value, country: v, county: null })}
        disabled={disabled}
      />
      <Group align="end" gap="xs" wrap="nowrap">
        <div style={{ flex: 1 }}>
          {showList ? (
            <Select
              label="County"
              placeholder="Select county"
              data={counties!}
              searchable
              clearable
              value={value.county}
              onChange={(v) => onChange({ ...value, county: v })}
              disabled={disabled}
            />
          ) : (
            <TextInput
              label="County"
              placeholder={counties ? 'Type county (manual entry)' : 'Type county'}
              value={value.county ?? ''}
              onChange={(e) => onChange({ ...value, county: e.currentTarget.value || null })}
              disabled={disabled}
            />
          )}
        </div>
        <Switch
          label="Manual"
          checked={value.countyManual}
          onChange={(e) => onChange({ ...value, countyManual: e.currentTarget.checked })}
          disabled={disabled}
        />
      </Group>
      <Autocomplete
        label="Street Address"
        placeholder="Street, number"
        data={streetSuggestions}
        value={value.street ?? ''}
        onChange={(v) => onChange({ ...value, street: v || null })}
        disabled={disabled}
      />
      <TextInput
        label="ZIP Code"
        value={value.zip ?? ''}
        onChange={(e) => onChange({ ...value, zip: e.currentTarget.value || null })}
        disabled={disabled}
      />
    </Stack>
  );
}

// During creation, new contacts need temporary negative ids so they can be
// tracked in a list before the customer exists on the server.
let pendingIdSeq = -1;
function nextPendingId() { return pendingIdSeq--; }

export default function CustomerModal({
  initial,
  onClose,
  onSaved,
  onDeleted,
}: {
  initial: Customer | null;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { me } = useAuth();
  const writable = canWrite(me?.role);

  const [loading, setLoading] = useState(!!initial);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [name, setName] = useState(initial?.name ?? '');
  const [logo, setLogo] = useState<string | null>(initial?.logoPath ?? null);

  const [addr, setAddr] = useState<AddressState>(() =>
    initial ? {
      country: initial.address.country,
      county: initial.address.county,
      street: initial.address.street,
      zip: initial.address.zip,
      countyManual: !!initial.address.county && !(countiesFor(initial.address.country)?.includes(initial.address.county ?? '')),
    } : emptyAddress()
  );

  const [legal, setLegal] = useState<LegalState>(() =>
    initial ? {
      companyName: initial.legal.companyName,
      vatNumber: initial.legal.vatNumber,
      country: initial.legal.country,
      county: initial.legal.county,
      street: initial.legal.street,
      zip: initial.legal.zip,
      countyManual: !!initial.legal.county && !(countiesFor(initial.legal.country)?.includes(initial.legal.county ?? '')),
    } : { ...emptyAddress(), companyName: null, vatNumber: null }
  );

  const [showAddress, setShowAddress] = useState(true);
  const [showLegal, setShowLegal] = useState(true);
  const [showContacts, setShowContacts] = useState(true);

  const [streetSuggestions, setStreetSuggestions] = useState<string[]>([]);
  const [editingContact, setEditingContact] = useState<Contact | 'new' | null>(null);

  // In-memory contact buffer used while creating a new customer.
  const [pendingContacts, setPendingContacts] = useState<Contact[]>([]);

  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!initial) return;
    setLoading(true);
    try {
      const d = await api.get<CustomerDetail>(`/customers/${initial.id}`);
      setDetail(d);
    } finally {
      setLoading(false);
    }
  }

  async function loadStreetSuggestions() {
    try {
      const all = await api.get<Customer[]>('/customers');
      const set = new Set<string>();
      for (const c of all) {
        if (c.address.street) set.add(c.address.street);
        if (c.legal.street) set.add(c.legal.street);
      }
      setStreetSuggestions(Array.from(set).sort());
    } catch { /* non-fatal */ }
  }

  useEffect(() => { load(); loadStreetSuggestions(); }, []);

  const contacts: Contact[] = initial ? (detail?.contacts ?? []) : pendingContacts;

  async function refreshContacts() {
    if (!initial) return;
    const d = await api.get<CustomerDetail>(`/customers/${initial.id}`);
    setDetail(d);
  }

  async function save() {
    setErr(null);
    if (!name.trim()) return setErr('Customer name is required.');
    const nameErr = folderNameError(name.trim());
    if (nameErr) return setErr(nameErr);
    setBusy(true);
    try {
      const body = {
        name: name.trim(),
        logoPath: logo,
        address: {
          country: addr.country,
          county: addr.county,
          street: addr.street,
          zip: addr.zip,
        },
        legal: {
          companyName: legal.companyName,
          vatNumber: legal.vatNumber,
          country: legal.country,
          county: legal.county,
          street: legal.street,
          zip: legal.zip,
        },
      };
      if (initial) {
        await api.put(`/customers/${initial.id}`, body);
      } else {
        const created = await api.post<Customer>('/customers', body);
        // Flush pending contacts against the newly created customer.
        for (const pc of pendingContacts) {
          try {
            await api.post(`/customers/${created.id}/contacts`, {
              name: pc.name,
              phone: pc.phone,
              email: pc.email,
              note: pc.note,
            });
          } catch (ex: any) {
            // Surface the failure but don't rollback — the customer exists and the user can retry adding the contact.
            notifications.show({ message: `Contact "${pc.name}" failed to save: ${ex.message ?? ex}`, color: 'red' });
          }
        }
      }
      notifications.show({ message: 'Customer saved.', color: 'green' });
      onSaved();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  function confirmDelete() {
    if (!initial) return;
    modals.openConfirmModal({
      title: `Delete ${initial.name}?`,
      children: 'This cannot be undone. All contacts for this customer will also be deleted.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.delete(`/customers/${initial.id}`);
          notifications.show({ message: 'Customer deleted.', color: 'gray' });
          onDeleted();
        } catch (e: any) {
          notifications.show({ message: e.message ?? 'Delete failed', color: 'red' });
        }
      },
    });
  }

  const dirty = useMemo(() => {
    if (!initial) {
      return (
        name.trim() !== '' || !!logo ||
        !!addr.country || !!addr.county || !!addr.street || !!addr.zip ||
        !!legal.companyName || !!legal.vatNumber || !!legal.country || !!legal.county || !!legal.street || !!legal.zip ||
        pendingContacts.length > 0
      );
    }
    return (
      name !== initial.name ||
      logo !== initial.logoPath ||
      addr.country !== initial.address.country || addr.county !== initial.address.county ||
      addr.street !== initial.address.street || addr.zip !== initial.address.zip ||
      legal.companyName !== initial.legal.companyName || legal.vatNumber !== initial.legal.vatNumber ||
      legal.country !== initial.legal.country || legal.county !== initial.legal.county ||
      legal.street !== initial.legal.street || legal.zip !== initial.legal.zip
    );
  }, [initial, name, logo, addr, legal, pendingContacts]);

  function confirmClose() {
    if (!dirty) return onClose();
    modals.openConfirmModal({
      title: 'Discard changes?',
      children: 'You will lose your edits.',
      labels: { confirm: 'Discard', cancel: 'Keep editing' },
      confirmProps: { color: 'red' },
      onConfirm: onClose,
    });
  }

  return (
    <Modal
      opened
      onClose={confirmClose}
      size="xl"
      title={<span className="modal-title-custom">{initial ? `Edit ${initial.name}` : 'Add Customer'}</span>}
    >
      {loading ? (
        <Center p="xl"><Loader /></Center>
      ) : (
        <Stack>
          <div className="section">
            <div className="section-header">Customer</div>
            <div className="section-body">
              <Stack>
                <ImageUpload entity="customers" value={logo} onChange={setLogo} label="Customer Logo" />
                <TextInput
                  data-tour="customer-name"
                  label="Customer Name"
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                  required
                  maxLength={160}
                  disabled={!writable}
                  error={name ? folderNameError(name) : null}
                />
              </Stack>
            </div>
          </div>

          <div className="section">
            <div
              className="section-header"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowAddress((v) => !v)}
            >
              Customer Address {showAddress ? '▾' : '▸'}
            </div>
            <Collapse in={showAddress}>
              <div className="section-body">
                <AddressEditor
                  title=""
                  value={addr}
                  onChange={setAddr}
                  streetSuggestions={streetSuggestions}
                  disabled={!writable}
                />
              </div>
            </Collapse>
          </div>

          <div className="section">
            <div
              className="section-header"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowLegal((v) => !v)}
            >
              Customer Legal Entity {showLegal ? '▾' : '▸'}
            </div>
            <Collapse in={showLegal}>
              <div className="section-body">
                <Stack>
                  <TextInput
                    label="Company Name"
                    value={legal.companyName ?? ''}
                    onChange={(e) => setLegal({ ...legal, companyName: e.currentTarget.value || null })}
                    maxLength={160}
                    disabled={!writable}
                  />
                  <TextInput
                    label="VAT Number"
                    value={legal.vatNumber ?? ''}
                    onChange={(e) => setLegal({ ...legal, vatNumber: e.currentTarget.value || null })}
                    maxLength={40}
                    disabled={!writable}
                  />
                  <AddressEditor
                    title="Legal Address"
                    value={legal}
                    onChange={(v) => setLegal({ ...legal, ...v })}
                    streetSuggestions={streetSuggestions}
                    disabled={!writable}
                  />
                </Stack>
              </div>
            </Collapse>
          </div>

          <div className="section">
            <div
              className="section-header"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowContacts((v) => !v)}
            >
              Customer Contacts {showContacts ? '▾' : '▸'}
            </div>
            <Collapse in={showContacts}>
              <div className="section-body">
                <Paper withBorder>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th style={{ width: 160 }}>Phone</Table.Th>
                        <Table.Th style={{ width: 220 }}>E-mail</Table.Th>
                        <Table.Th>Note</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {contacts.map((c) => (
                        <Table.Tr key={c.id} className="click-row" onClick={() => setEditingContact(c)}>
                          <Table.Td>{c.name}</Table.Td>
                          <Table.Td>{c.phone ?? '—'}</Table.Td>
                          <Table.Td>{c.email ?? '—'}</Table.Td>
                          <Table.Td>{c.note ?? '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                      <Table.Tr>
                        <Table.Td colSpan={4}>
                          <Button
                            data-tour="customer-add-contact"
                            fullWidth
                            variant="light"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => setEditingContact('new')}
                            disabled={!writable}
                          >
                            Add Contact
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tbody>
                  </Table>
                </Paper>
                {!initial && pendingContacts.length > 0 && (
                  <Text size="xs" c="dimmed" mt="xs">
                    Contacts are held in memory and will be saved when you Save the customer.
                  </Text>
                )}
              </div>
            </Collapse>
          </div>

          {err && <Alert color="red">{err}</Alert>}

          <Group justify="space-between" mt="md">
            <div>
              {initial && writable && (
                <Button color="red" variant="light" onClick={confirmDelete}>Delete</Button>
              )}
            </div>
            <Group>
              <Button variant="default" onClick={confirmClose}>Close</Button>
              {writable && <Button onClick={save} loading={busy} data-tour="customer-save">Save</Button>}
            </Group>
          </Group>
        </Stack>
      )}

      {editingContact && (
        <ContactModal
          customerId={initial?.id}
          initial={editingContact === 'new' ? null : editingContact}
          onClose={() => setEditingContact(null)}
          onDone={async () => { await refreshContacts(); setEditingContact(null); }}
          onLocalSave={!initial ? (c) => {
            setPendingContacts((prev) => {
              const exists = editingContact !== 'new' ? prev.find((p) => p.id === (editingContact as Contact).id) : null;
              if (exists) {
                return prev.map((p) => p.id === exists.id ? { ...p, ...c } : p);
              }
              return [...prev, { ...c, id: nextPendingId(), customerId: 0 }];
            });
            setEditingContact(null);
          } : undefined}
          onLocalDelete={!initial && editingContact !== 'new' ? () => {
            const target = editingContact as Contact;
            setPendingContacts((prev) => prev.filter((p) => p.id !== target.id));
            setEditingContact(null);
          } : undefined}
        />
      )}
    </Modal>
  );
}
